import { CnCodeSelector } from '@/shared/components/CnCodeSelector'
import { useGoodsCodes } from '@/shared/hooks/goodsCodes'
import {
  GoodsFamily,
  InputGroup,
  ListboxGroup,
  productionMethods,
  getInstallationReportingPeriods,
  ReportingPeriodBasis,
  generatePeriodId,
  aggregatedGoodCategoriesByGoodFamilies
} from '@/common'
import { isReportingPeriodEnded } from '@artem-cbam-declarants/shared'
import { useCallback, useEffect, useMemo } from 'react'
import useI18n from '@/shared/hooks/usei18n'
import { SupplierEmissionsReportFormData } from './SupplierEmissionsReportForm.schema'

export default function SupplierEmissionsReportGoodsDataForm({
  data,
  onChange,
  startMonth,
  monthInterval,
  goodsFamily,
  basisForReportingPeriod,
  isEditMode = false,
  originalPeriodId,
  goodsDataLocked = false
}: {
  data: SupplierEmissionsReportFormData
  onChange: (data: SupplierEmissionsReportFormData) => void
  startMonth: number
  monthInterval: number
  goodsFamily: GoodsFamily
  basisForReportingPeriod: ReportingPeriodBasis
  isEditMode?: boolean
  /** Period stored on the report when edit opened; unchanged for the session (see legacy option below). */
  originalPeriodId?: string
  goodsDataLocked?: boolean
}) {
  const { t, dateLongFormatterMonthYear } = useI18n()

  const { data: goodsCodes, refetch: refetchGoodsCodes } = useGoodsCodes({
    productionMethod: data.productionMethod
  })

  useEffect(() => {
    if (data.productionMethod) {
      refetchGoodsCodes()
    }
  }, [data.productionMethod, refetchGoodsCodes])

  const reportingPeriodOptions = useMemo(() => {
    const allOptions = getInstallationReportingPeriods(startMonth, monthInterval).map(period => ({
      label:
        basisForReportingPeriod === 'CALENDAR_YEAR'
          ? `${period.startDate.getFullYear()}`
          : `${dateLongFormatterMonthYear(period.startDate)} - ${dateLongFormatterMonthYear(period.endDate)}`,
      value: generatePeriodId(period.startDate, period.endDate)
    }))

    const eligibleOptions = allOptions.filter(option => isReportingPeriodEnded(option.value))

    // Reports saved before this guard may still have an in-progress period (originalPeriodId).
    // Keep that option visible (disabled) so the label renders, but block re-selecting it after
    // the user changes away — keyed on originalPeriodId, not the current form value.
    if (
      isEditMode &&
      originalPeriodId &&
      !isReportingPeriodEnded(originalPeriodId) &&
      !eligibleOptions.some(option => option.value === originalPeriodId)
    ) {
      const legacyOption = allOptions.find(option => option.value === originalPeriodId)
      if (legacyOption) {
        return [{ ...legacyOption, disabled: true }, ...eligibleOptions]
      }
    }

    return eligibleOptions
  }, [
    startMonth,
    monthInterval,
    basisForReportingPeriod,
    dateLongFormatterMonthYear,
    isEditMode,
    originalPeriodId
  ])

  const productionMethodsForCategory = useMemo(
    () =>
      productionMethods.filter(method => method.aggregatedGoodsCategory === data.aggregatedGoods),
    [data.aggregatedGoods]
  )


  const aggregatedGoodsOptions = goodsFamily
    ? aggregatedGoodCategoriesByGoodFamilies[goodsFamily].map(aggregatedGood => ({
        label: t(`common:aggregatedGoodCategories.${aggregatedGood.value}`),
        value: aggregatedGood.value
      }))
    : []

  const handleAggregatedGoodsChange = useCallback(
    (value: string) => {
      const relevantProductionMethods = productionMethods.filter(
        method => method.aggregatedGoodsCategory === value
      )

      if (relevantProductionMethods.length === 1) {
        onChange({
          ...data,
          aggregatedGoods: value,
          productionMethod: relevantProductionMethods[0].code,
          cnCode: ''
        })
      } else {
        onChange({
          ...data,
          aggregatedGoods: value,
          productionMethod: '',
          cnCode: ''
        })
      }
    },
    [data, onChange]
  )

  const productionMethodOptions = useMemo(() => {
    return (
      productionMethodsForCategory.map(method => ({
        label: t(`common:productionMethods.${method.code}`),
        value: method.code
      })) || []
    )
  }, [productionMethodsForCategory, t])

  const onProductionMethodChange = useCallback(
    (value: string) => {
      onChange({
        ...data,
        productionMethod: value,
        cnCode: ''
      })
    },
    [data, onChange]
  )

  const productionMethodsCnCodes = useMemo(() => {
    return goodsCodes?.map(goodsCode => goodsCode.goodsCode)
  }, [goodsCodes])

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-4">
        <ListboxGroup
          required
          hidePlaceholder
          disabled={goodsDataLocked}
          label={t('supplierEmissionsReport:supplierEmissionsReportInputFields.reportingPeriod')}
          options={reportingPeriodOptions}
          value={data.periodId}
          onChange={value => onChange({ ...data, periodId: value })}
        />
        <ListboxGroup
          disabled={goodsDataLocked}
          label={t('supplierEmissionsReport:supplierEmissionsReportInputFields.aggregatedGoods')}
          options={aggregatedGoodsOptions}
          value={data.aggregatedGoods || ''}
          onChange={value => handleAggregatedGoodsChange(value)}
        />
        <ListboxGroup
          disabled={
            goodsDataLocked || productionMethodOptions.length <= 1 || data.aggregatedGoods === ''
          }
          label={t('supplierEmissionsReport:supplierEmissionsReportInputFields.productionMethod')}
          options={productionMethodOptions}
          value={data.productionMethod || ''}
          onChange={value => onProductionMethodChange(value)}
        />
      </div>
      <CnCodeSelector
        disabled={goodsDataLocked || data.productionMethod === ''}
        onCnCodeUpdate={value => onChange({ ...data, cnCode: value })}
        label={t('supplierEmissionsReport:supplierEmissionsReportInputFields.cnCode')}
        cnCode={data.cnCode || ''}
        filteredCnCodes={productionMethodsCnCodes}
      />
      <InputGroup
        label={t('supplierEmissionsReport:supplierEmissionsReportInputFields.goodsDescription')}
        value={data.goodsDescription}
        onChange={value => onChange({ ...data, goodsDescription: value.target.value })}
      />
    </div>
  )
}
